import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dialog } from 'primereact/dialog';
import { RingilH2 } from '@components/styles';
import useForm from '../../../../hooks/useForm/useForm';
import { CarrierGroupInDTO } from '@api/ringil-api.v2';
import dayjs, { Dayjs } from 'dayjs';
import { Button } from 'primereact/button';
import { apiPragueFromDayjsLocal, apiUtcFromDayjs, dayjsNowInTz } from '@utils/dateWithTz.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import { SelectCarriers } from '@components/obsolete/SelectCarriers/SelectCarriers.tsx';
import InputDeadline, { DeadlineMode, DeadlineUnit } from '@components/RingilComponents/CarrierDistributionSelector/InputDeadline.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';

interface ComponentProps {
  onCompleted: () => void;
  groupDemandId: string;
  timezoneId: string;
}

interface FormData {
  selectedCarriersAndGroups: CarrierGroupInDTO[];
  deadlineMode: DeadlineMode;
  offsetValue: number;
  offsetUnit: DeadlineUnit;
  utcDateTime: Dayjs;
  timezoneId: string;
}
export const useCreateMDDistributionDialog = () => {
  const [visible, setVisible] = useState(false);

  const CreateMDDistributionDialog: React.FC<ComponentProps> = ({ groupDemandId, timezoneId, onCompleted }) => {
    const { tr } = useTranslationLgs();
    const { createGroupDemandsDistribution } = useContext(ApiContext);

    const form = useForm<FormData>(
      yup.object().shape({}),
      {
        selectedCarriersAndGroups: [],
        deadlineMode: 'relative',
        offsetValue: 4,
        offsetUnit: 'hours',
        utcDateTime: dayjsNowInTz(timezoneId).add(4, 'hour'),
        timezoneId: timezoneId,
      },
      data => save(data),
      true,
      false,
    );

    const resolveDeadline = (data: FormData) => {
      const tz = data.timezoneId || 'Europe/Prague';
      if (data.deadlineMode === 'relative') {
        const offsetHours = data.offsetUnit === 'days' ? data.offsetValue * 24 : data.offsetValue;
        return { localTime: dayjs().add(offsetHours, 'hour'), timezone: tz };
      }
      return { localTime: data.utcDateTime, timezone: tz };
    };

    const save = (data: FormData) => {
      const resolved = resolveDeadline(data);
      createGroupDemandsDistribution(
        groupDemandId,
        {
          validTill: apiPragueFromDayjsLocal(resolved.localTime),
          utcEnd: apiUtcFromDayjs(resolved.localTime),
          timezoneId: resolved.timezone,
          selectedItems: data.selectedCarriersAndGroups,
        },
        () => {
          setVisible(false);
          onCompleted();
        },
      );
    };
    return (
      <>
        {/*
        // @ts-ignore*/}
        <Dialog onHide={() => setVisible(false)} visible={visible}>
          <RingilH2>{tr(`CreateMDDistrobutionDialog.inquireCarrier`, `Inquire carrier`)}</RingilH2>
          <HorizontalFieldValue
            label={tr(`CreateMDDistrobutionDialog.whoInquire`, `Who inquire`)}
            value={
              <SelectCarriers
                value={form.values.selectedCarriersAndGroups}
                onSelected={v =>
                  form.setFieldValue(
                    'selectedCarriersAndGroups',
                    v.map(item => ({ ...item, userId: item.userIds?.[0] ?? null })),
                  )
                }
                onlyCarriers={false}
                isDisabled={false}
                placeholder={''}
                testIdBase="gd-demand-selectCarriers-carrierList"
              />
            }
          ></HorizontalFieldValue>
          <HorizontalFieldValue
            label={tr(`CreateMDDistrobutionDialog.untilWhenDoUouNeedAPriceOffer`, `Until when do uou need a price offer`)}
            value={
              <InputDeadline
                mode={form.values.deadlineMode}
                offsetValue={form.values.offsetValue}
                offsetUnit={form.values.offsetUnit}
                absoluteLocalTime={form.values.utcDateTime}
                absoluteTimezone={form.values.timezoneId}
                onChangeRelative={(value, unit) =>
                  form.useProduce(draft => {
                    draft.offsetValue = value;
                    draft.offsetUnit = unit;
                  })
                }
                onChangeAbsolute={(localDate, tz) =>
                  form.useProduce(draft => {
                    draft.utcDateTime = localDate;
                    if (tz) draft.timezoneId = tz;
                    draft.deadlineMode = 'absolute';
                  })
                }
                onModeChange={mode => form.setFieldValue('deadlineMode', mode)}
                dataTestIdCalendar='gd-demand-selectCarriers-validTill-calendar'
                dataTestIdTimezone='gd-demand-selectCarriers-validTill-timezone'
              />
            }
          />
          <ButtonLayout wrapper="WITH_PADDING_TOP_0">
            <Button data-testid="gd-demand-selectCarriers-confirm" label={tr(`CreateMDDistributionDialog.confirm`, `Potvrdit`)} className="p-mr-3" onClick={() => form.validateAndSend()} />
            <Button data-testid="gd-demand-selectCarriers-cancel" label={tr(`CreateMDDistributionDialog.cancel`, `Zrušit`)} className="p-button-outlined p-button-text" onClick={() => setVisible(false)} />
          </ButtonLayout>
        </Dialog>
      </>
    );
  };

  return { CreateMDDistributionDialog, setVisible };
};
